import React, { useState, useMemo, useRef } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { TrendingUp } from "lucide-react";
import { generateDateTicks } from "../../../services/dataTicks";

const kpiOptions = [
  { key: "ciiScore", label: "CII Score", color: "#27ae60" },
  { key: "fuelEUScore", label: "FuelEU Score", color: "rgb(26, 82, 118)" },
  { key: "euETSScore", label: "EU ETS Score", color: "rgb(125, 60, 152)" },
];

const ciiGradientStops = [
  [0, "#27ae60"],
  [0.2, "#2ecc71"],
  [0.4, "#f1c40f"],
  [0.6, "#f39c12"],
  [0.8, "#e74c3c"],
  [1, "#c0392b"],
];

const hexToRgb = (hex) => {
  const cleanHex = hex.replace("#", "");
  const value = parseInt(cleanHex, 16);
  return {
    r: (value >> 16) & 255,
    g: (value >> 8) & 255,
    b: value & 255,
  };
};

const rgbToHex = ({ r, g, b }) =>
  `#${[r, g, b]
    .map((v) => Math.round(v).toString(16).padStart(2, "0"))
    .join("")}`;

const interpolateColor = (startColor, endColor, progress) => {
  const s = hexToRgb(startColor);
  const e = hexToRgb(endColor);
  return rgbToHex({
    r: s.r + (e.r - s.r) * progress,
    g: s.g + (e.g - s.g) * progress,
    b: s.b + (e.b - s.b) * progress,
  });
};

const getCIIScoreColor = (score) => {
  const val = Math.min(Math.max(score || 0, 0), 1);

  for (let i = 1; i < ciiGradientStops.length; i++) {
    const [currStop, currColor] = ciiGradientStops[i];
    const [prevStop, prevColor] = ciiGradientStops[i - 1];

    if (val <= currStop) {
      const progress =
        (val - prevStop) / (currStop - prevStop);
      return interpolateColor(prevColor, currColor, progress);
    }
  }

  return ciiGradientStops[ciiGradientStops.length - 1][1];
};


const getDynamicCIIGradient = (data) => {
  if (!data?.length) return "";

  const values = data.map((d) => d.ciiScore ?? 0);
  const max = Math.max(...values);

  const steps = 6;
  const stops = [];

  for (let i = 0; i <= steps; i++) {
    const v = (max / steps) * i;
    const percent = (i / steps) * 100;
    stops.push(`${getCIIScoreColor(v)} ${percent}%`);
  }

  return `linear-gradient(to right, ${stops.join(",")})`;
};

const getCIIScoreZones = () => {
  const zones = [];
  for (let v = 0.05; v <= 1; v += 0.05) {
    zones.push({
      value: Number(v.toFixed(2)),
      color: getCIIScoreColor(v),
    });
  }
  zones.push({ color: getCIIScoreColor(1) });
  return zones;
};



const KPITrendsChart = ({ kpiTrends }) => {
  const prevDataRef = useRef(null);

  const dataToUse = useMemo(() => {
    if (Array.isArray(kpiTrends)) {
      if (kpiTrends.length > 0) {
        prevDataRef.current = kpiTrends;
      }
      return kpiTrends;
    }

    if (prevDataRef.current?.length) {
      return prevDataRef.current;
    }

    return [];
  }, [kpiTrends]);

  const dates = dataToUse.map((item) => item.date);
  const tickPositions = generateDateTicks(dates);

  const [selectedKPIs, setSelectedKPIs] = useState([
    "ciiScore",
    "fuelEUScore",
    "euETSScore",
  ]);

  const handleToggleKPI = (kpi) => {
    setSelectedKPIs((prev) =>
      prev.includes(kpi)
        ? prev.filter((k) => k !== kpi)
        : [...prev, kpi]
    );
  };

  const generateSeries = useMemo(() => {
    return () => {
      const data = dataToUse || [];

      return selectedKPIs.map((kpi) => {
        const kpiConfig = kpiOptions.find((opt) => opt.key === kpi);

        const seriesData = data.map((trend) => {
          const y = trend[kpi] ?? 0;
          const ciiColor = getCIIScoreColor(y);

          return {
            x: new Date(trend.date).getTime(),
            y,
            ciiRating: trend.ciiRating || null,
            color: kpi === "ciiScore" ? ciiColor : kpiConfig.color,
            marker: kpi === "ciiScore" ? { fillColor: ciiColor } : undefined,
          };
        });

        return {
          name: kpiConfig.label,
          type: "spline",
          data: seriesData,
          color: kpiConfig.color,
          zoneAxis: kpi === "ciiScore" ? "y" : undefined,
          zones: kpi === "ciiScore" ? getCIIScoreZones() : undefined,
          lineWidth: 3,
          marker: {
            enabled: true,
            symbol: "circle",
            radius: 6,
            lineWidth: 2,
            lineColor: "#fff",
          },
        };
      });
    };
  }, [dataToUse, selectedKPIs]);

  const chartOptions = useMemo(() => {
    const gradient = getDynamicCIIGradient(dataToUse);

    return {
      chart: {
        type: "line",
        zoomType: "xy",
        backgroundColor: "transparent",
        height: 500,
      },

      title: { text: null },

      xAxis: {
        type: "datetime",
        tickPositions,
        labels: { format: "{value:%b %d}" },
        gridLineWidth: 1,
        gridLineColor: "#f3f4f6",
      },

      yAxis: {
        min: 0,
        max: 1.2,
        tickPositions: [0, 0.2, 0.4, 0.6, 0.8, 1, 1.2],
        title: { text: "KPI Values" },
      },

      tooltip: {
        useHTML: true,
        shared: true,
        headerFormat: "<b>{point.key:%Y-%m-%d}</b><br>",
      },

      legend: {
        enabled: true,
        align: "right",
        verticalAlign: "top",
        useHTML: true,
        symbolWidth: 0, 

        labelFormatter: function () {
          if (this.name === "CII Score") {
            return `
              <span style="display:flex;align-items:center;gap:8px;">
                <span style="
                  width:10px;
                  height:10px;
                  border-radius:8px;
                  background:${gradient};
                "></span>
                ${this.name}
              </span>
            `;
          }

          return `
            <span style="display:flex;align-items:center;gap:8px;">
              <span style="
                width:10px;
                height:10px;
                border-radius:50%;
                background:${this.color};
              "></span>
              ${this.name}
            </span>
          `;
        },
      },

      series: generateSeries(),
      credits: { enabled: false },
    };
  }, [generateSeries, tickPositions, dataToUse]);

  return (
    <div>
      <div className="flex flex-col gap-4 mb-6">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-[#27ade3]" />
            <span className="text-base font-semibold text-gray-700">
              Select KPIs:
            </span>
          </div>

          <div className="flex flex-wrap gap-3">
            {kpiOptions.map((kpi) => {
              const isSelected = selectedKPIs.includes(kpi.key);

              return (
                <button
                  key={kpi.key}
                  onClick={() => handleToggleKPI(kpi.key)}
                  className={`px-4 py-2 text-sm rounded-lg ${
                    isSelected
                      ? "text-white shadow-md"
                      : "bg-gray-50 text-gray-600 border"
                  }`}
                  style={{
                    backgroundColor: isSelected ? kpi.color : undefined,
                  }}
                >
                  {kpi.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <HighchartsReact highcharts={Highcharts} options={chartOptions} />
    </div>
  );
};

export default KPITrendsChart;